import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Upload, Activity, FileText, CreditCard, Search, Eye, ShieldCheck, ChevronDown, ChevronRight, HelpCircle, Camera, FolderOpen } from 'lucide-react';

const steps = [
  {
    icon: Upload,
    title: '1. Upload a Chest X-Ray',
    content: 'Navigate to "New Case" from the sidebar. You can either upload an existing X-ray image (PNG/JPG) from your device, or on mobile/tablet, use the camera to capture a photo of the X-ray directly. Drag-and-drop is also supported on desktop.',
  },
  {
    icon: Activity,
    title: '2. Enter Patient Details',
    content: 'Fill in the patient name, age, gender, and any clinical notes. You can select an existing patient from your records or create a new one. Existing patients are linked automatically for tracking history across visits.',
  },
  {
    icon: Eye,
    title: '3. Run AI Analysis',
    content: 'Click "Run AI Analysis" on the case page. The AI (powered by GPT-5.2 Vision) will analyze the X-ray for 10+ conditions including Pneumonia, Cardiomegaly, Pleural Effusion, TB indicators, and more. Results appear in seconds.',
  },
  {
    icon: ShieldCheck,
    title: '4. Review Results',
    content: 'View multi-disease probabilities with severity badges. Toggle Bounding Boxes to see detected regions, Grad-CAM heatmaps for attention areas, and Lung Region estimation. Each finding includes a GP-interpretable description.',
  },
  {
    icon: FileText,
    title: '5. Download PDF Report',
    content: 'Generate a detailed PDF report with patient info, X-ray image, all findings with probabilities, summary, recommendations, and a medical disclaimer. Share with patients or attach to medical records.',
  },
  {
    icon: FolderOpen,
    title: '6. Track Patient History',
    content: 'All cases are stored with unique Patient IDs (PID-XXXXXX). Click any Patient ID in Case History to see the complete timeline of all X-ray analyses for that patient.',
  },
  {
    icon: CreditCard,
    title: '7. Manage Subscription',
    content: 'New accounts get a Demo Plan with 5 free analyses. After that, choose Pay-Per-Case (Rs 15), or subscribe for unlimited analyses: 3 months (Rs 2,999), 6 months (Rs 5,500), or 1 year (Rs 9,999). Pay via Razorpay or UPI QR.',
  },
];

const faqs = [
  { q: 'Is this a replacement for a radiologist?', a: 'No. ChestBuddy is a decision support system. All AI findings must be verified by the attending physician. It assists GPs in preliminary screening and prioritization.' },
  { q: 'What image formats are supported?', a: 'PNG and JPG/JPEG formats. On mobile devices, you can also capture X-ray photos directly using your camera.' },
  { q: 'How accurate is the AI analysis?', a: 'The AI evaluates for 10+ chest conditions with probability scores. Results are indicative and should be correlated with clinical findings. Accuracy varies by image quality and condition.' },
  { q: 'Can I use this for pediatric X-rays?', a: 'The AI is primarily trained on adult chest X-rays. Pediatric interpretations should be used with extra caution and always verified by a specialist.' },
  { q: 'How is patient data stored?', a: 'All data is stored securely. X-ray images are stored on our servers. Each case is linked to your doctor account and not accessible by other users.' },
  { q: 'What happens when my credits run out?', a: 'You will be redirected to the Subscription page to purchase more credits or subscribe to a plan before continuing with analysis.' },
];

export default function HelpPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="help-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <HelpCircle className="w-6 h-6 text-[#1A4D2E]" />
          <h1 className="text-3xl font-heading font-medium tracking-tight text-stone-900">Help & User Guide</h1>
        </div>
        <p className="text-stone-500 mt-1">Learn how to use ChestBuddy: ChestAI GP-Assist effectively</p>
      </div>

      {/* Step-by-step guide */}
      <Card className="border-stone-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">How to Use ChestBuddy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {steps.map((step, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center shrink-0 mt-0.5">
                <step.icon className="w-5 h-5 text-[#1A4D2E]" />
              </div>
              <div>
                <h3 className="text-sm font-heading font-semibold text-stone-900">{step.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed mt-1">{step.content}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Mobile Camera tip */}
      <Card className="border-[#1A4D2E]/20 shadow-sm bg-[#1A4D2E]/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#1A4D2E] flex items-center justify-center shrink-0">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-heading font-semibold text-stone-900">Mobile Camera Capture</h3>
              <p className="text-sm text-stone-600 leading-relaxed mt-1">
                On phones and tablets, you will see a <strong>"Capture with Camera"</strong> button on the upload page. This opens your device camera directly so you can photograph an X-ray film on a lightbox. Ensure good lighting and minimize glare for best results.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQs */}
      <Card className="border-stone-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {faqs.map((faq, i) => (
            <div key={i} className="border-b border-stone-100 last:border-0">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between py-3 text-left"
                data-testid={`faq-${i}`}
              >
                <span className="text-sm font-medium text-stone-900 pr-4">{faq.q}</span>
                {openFaq === i ? <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />}
              </button>
              {openFaq === i && (
                <p className="text-sm text-stone-600 pb-3 leading-relaxed">{faq.a}</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm">
        <strong>Medical Disclaimer:</strong> ChestBuddy is an AI-assisted decision support system. It does not provide medical diagnoses. All findings must be independently verified by a qualified healthcare professional. The practitioner bears full responsibility for clinical decisions.
      </div>
    </div>
  );
}
